/**
 * The devotee console — `/v1/admin/users*`.
 *
 * **Nothing here holds an unmasked identity except one response.** The list and profile carry
 * `masked_phone`, `masked_email`, `masked_name` and counts; `useRevealField` is the single call
 * that returns a real value, and the server has already written an audit row naming the operator,
 * the support case and the reason before it answers.
 *
 * `useRevealField` is a **mutation, not a query**, for that reason. Caching it would mean a second
 * read of somebody's gotra that never reached the audit log — and the log is the only thing that
 * can answer "who looked at this person's data, and why".
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type UserRow = components['schemas']['UserRow'];
export type UserDetail = components['schemas']['UserDetailResponse'];
type UserList = components['schemas']['UserListResponse'];
export type UserAggregates = components['schemas']['UserAggregatesResponse'];
export type ConsentEntry = components['schemas']['ConsentEntryOut'];
type UserConsents = components['schemas']['UserConsentsResponse'];

export interface UserQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  q?: string;
  status?: string;
}

/** Exported for its own test — an absent filter is omitted, never sent empty. */
export function userQueryString(params: UserQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.q) search.set('q', params.q);
  if (params.status) search.set('status', params.status);
  return search.toString();
}

/** `enabled: false` for a lookup that should read nothing until somebody has typed. */
export function useUsers(params: UserQuery, enabled = true) {
  return useQuery({
    queryKey: keys.users.list(params),
    queryFn: () => authedClient.request<UserList>(`/v1/admin/users?${userQueryString(params)}`),
    enabled,
  });
}

/** Header counts and one ratio — never an id, a name or a phone (`/v1/admin/users/aggregates`). */
export function useUserAggregates() {
  return useQuery({
    queryKey: keys.users.aggregates(),
    queryFn: () => authedClient.request<UserAggregates>('/v1/admin/users/aggregates'),
  });
}

export function useUser(id: number | undefined) {
  return useQuery({
    queryKey: keys.users.one(id ?? 0),
    queryFn: () => authedClient.request<UserDetail>(`/v1/admin/users/${id}`),
    enabled: id !== undefined,
  });
}

/**
 * The consent ledger for one account — read-only; the console never grants consent. Mounted only
 * inside an open profile, so the id is always known.
 */
export function useUserConsents(id: number) {
  return useQuery({
    queryKey: keys.users.consents(id),
    queryFn: () => authedClient.request<UserConsents>(`/v1/admin/users/${id}/consents`),
  });
}

/**
 * Decrypt one field. A mutation because it has a side effect on the server — the audit row — and
 * because the value must never be cached alongside the masked profile that deliberately omits it.
 */
export function useRevealField() {
  return useMutation({
    mutationFn: ({
      id,
      field,
      supportCase,
      reason,
    }: {
      id: number;
      field: string;
      supportCase: string;
      reason: string;
    }) =>
      authedClient.request<components['schemas']['RevealResponse']>(
        `/v1/admin/users/${id}/decrypt-field`,
        { method: 'POST', body: { field, support_case: supportCase, reason } },
      ),
  });
}

function useUserMutation<TArgs>(fn: (args: TArgs) => Promise<unknown>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.users.all() }),
  });
}

export function useSetSuspended() {
  return useUserMutation(({ id, suspended, reason }: { id: number; suspended: boolean; reason: string }) =>
    authedClient.request<components['schemas']['UserStatusResponse']>(
      `/v1/admin/users/${id}/${suspended ? 'suspend' : 'unsuspend'}`,
      { method: 'POST', body: { reason } },
    ),
  );
}
